import { IMenuItemStyles } from "@fluentui/react/lib/ContextualMenu";
import { IVerticalDividerClassNames } from "@fluentui/react/lib/Divider";
import {
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  ITheme,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  baseFabricFocusSelector,
  commonHighContrastDisabledStyles,
  commonHighContrastFocusedDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  commonHighContrastOutlineToggledOn,
  controlStylingConstants,
  getDividerClassNames,
  getMenuButtonStateStyles,
  getStandardControlSemanticColorsWithTheme,
  IMenuItemClassNames,
  StandardControlSemanticColors,
} from "@1js/acui-common";
import { isThemingEnabled } from "@1js/acui-theme";

import {
  ControlInMenuStylesFunction,
  getClassNamesForMenuButton,
  getStylesFromControlInMenuFnOrObject,
} from "../AppBaseButton/AppBaseButtonInMenu.classNames";
import { baseSplitButtonExpandedCheckedIconStyle } from "./AppSplitButton.classNames";

const dividerClassName = "split-divider";
const splitContainerClassName = "split-container";
const splitPrimaryInMenuClassName = "splitPrimaryMenu";
const splitExpandButtonInMenuClassName = "splitButtonMenu";

const menuItemHeightWithoutBorder = controlStylingConstants.menuItemHeight - 2;

export const getSplitButtonVerticalDividerClassNames = memoizeFunction(
  (theme: ITheme): IVerticalDividerClassNames => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return mergeStyleSets(getDividerClassNames(theme), {
      divider: [
        dividerClassName,
        {
          height: 20,
          width: 1,
          backgroundColor:
            standardControlSemanticColors.menuDividerBackgroundColor,
        },
      ],
    });
  }
);

const redlineClassNamesForSplitMenuButton = (
  theme: ITheme,
  disabled: boolean,
  expanded: boolean,
  checked: boolean,
  primaryDisabled?: boolean
): Partial<IMenuItemClassNames> => {
  const expandedCheckedPrimaryButtonIconStyle =
    baseSplitButtonExpandedCheckedIconStyle(disabled || primaryDisabled);
  const splitButtonExpandedCheckedHCIconStyle = {
    selectors: {
      [`.${splitPrimaryInMenuClassName}`]:
        expandedCheckedPrimaryButtonIconStyle,
      [`.${splitExpandButtonInMenuClassName}`]:
        baseSplitButtonExpandedCheckedIconStyle(disabled),
    },
  };
  const containerFocusWithChildHoverSelector = `.${splitContainerClassName}:focus &:hover, ${baseFabricFocusSelector} .${splitContainerClassName}:focus &:hover`;
  const containerHoverSelectorFromExpanded = expanded
    ? ":hover"
    : ":focus:hover";
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  return mergeStyleSets({
    linkContentMenu: {
      whiteSpace: "nowrap",
      height: "inherit",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      maxWidth: "100%",
    },
    splitPrimary: [
      splitPrimaryInMenuClassName,
      {
        pointerEvents: "auto",
        font: "inherit",
        color: "inherit",
        width: "100%",
        textAlign: "left",
        backgroundColor: "transparent",
        border: "none",
        borderRadius: 0,
        display: "block",
        height: controlStylingConstants.menuItemHeight,
        lineHeight: controlStylingConstants.menuItemHeight,
        padding: "0 0 0 9px",
        overflow: "auto",
      },
      getMenuButtonStateStyles(theme, expanded, disabled),
      {
        selectors: {
          ":active": [
            {
              backgroundColor: `${standardControlSemanticColors.pressedBackgroundColor}`,
            },
            commonHighContrastHoverFocusStyles,
          ],
          [`${containerFocusWithChildHoverSelector}`]: {
            backgroundColor: standardControlSemanticColors.hoverBackgroundColor,
            selectors: {
              [HighContrastSelector]: {
                backgroundColor: "Highlight",
                color: "HighlightText",
                ...getHighContrastNoAdjustStyle(),
              },
            },
          },
          ":hover": commonHighContrastHoverFocusStyles,
          ":focus": commonHighContrastHoverFocusStyles,
          [HighContrastSelector]: {
            color: "ButtonText",
            ...getHighContrastNoAdjustStyle(),
          },
        },
      },
      (disabled || primaryDisabled) && {
        color: standardControlSemanticColors.textDisabledColor,
        pointerEvents: "none",
        userSelect: "none",
      },
      checked && {
        backgroundColor: standardControlSemanticColors.toggledBackgroundColor,
        border: `1px solid ${standardControlSemanticColors.selectedBorderColor}`,
        selectors: {
          ":hover": [
            {
              backgroundColor:
                standardControlSemanticColors.toggledBackgroundColor,
              border: `1px solid ${standardControlSemanticColors.selectedBorderColor}`,
            },
            commonHighContrastHoverFocusStyles,
          ],
        },
      },
      expanded && {
        selectors: {
          ":hover": [
            {
              backgroundColor:
                standardControlSemanticColors.hoverBackgroundColor,
            },
            commonHighContrastHoverFocusStyles,
          ],
          ":active": [
            {
              backgroundColor: `${standardControlSemanticColors.pressedBackgroundColor}`,
            },
            commonHighContrastHoverFocusStyles,
          ],
        },
      },
      (expanded || checked) && commonHighContrastHoverFocusStyles,
    ],
    icon: (disabled || primaryDisabled) && {
      opacity: 0.4,
      selectors: {
        [HighContrastSelector]: {
          opacity: 1,
          ...getHighContrastNoAdjustStyle(),
        },
      },
    },
    label: [
      {
        paddingRight: 10,
      },
      disabled && commonHighContrastDisabledStyles,
    ],
    labelDisabled: [
      {
        color: standardControlSemanticColors.textDisabledColor,
      },
      commonHighContrastDisabledStyles,
    ],
    splitMenu: [
      splitExpandButtonInMenuClassName,
      {
        pointerEvents: "auto",
        padding: 0,
        color: "inherit",
        backgroundColor: "transparent",
        border: "none",
        borderRadius: 0,
        display: "block",
        height: controlStylingConstants.menuItemHeight,
        lineHeight: controlStylingConstants.menuItemHeight,
        width: 39,
        selectors: {
          [`${containerFocusWithChildHoverSelector}`]: [
            {
              backgroundColor:
                standardControlSemanticColors.hoverBackgroundColor,
            },
            commonHighContrastHoverFocusStyles,
          ],
          ":focus": commonHighContrastHoverFocusStyles,
          ":active": commonHighContrastHoverFocusStyles,
          ":hover": commonHighContrastHoverFocusStyles,
        },
      },
      disabled && {
        color: standardControlSemanticColors.textDisabledColor,
        pointerEvents: "none",
        userSelect: "none",
      },
      getMenuButtonStateStyles(theme, expanded, disabled),
      checked &&
        !expanded && [
          {
            selectors: {
              backgroundColor:
                standardControlSemanticColors.defaultBackgroundColor, // This is unset when we put focus on the split container
              ":active": [
                {
                  backgroundColor: `${standardControlSemanticColors.pressedBackgroundColor} !important`, // !important needed because the split container also provides active styles that are more specific...
                },
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
        ],
      checked &&
        expanded && [
          {
            selectors: {
              ":hover": [
                {
                  backgroundColor:
                    standardControlSemanticColors.expandedBackgroundColor,
                },
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
          commonHighContrastHoverFocusStyles,
        ],
      !checked &&
        expanded && [
          {
            selectors: {
              ":hover": [
                {
                  backgroundColor:
                    standardControlSemanticColors.hoverBackgroundColor,
                },
                commonHighContrastHoverFocusStyles,
              ],
              ":active": commonHighContrastHoverFocusStyles,
            },
          },
          commonHighContrastHoverFocusStyles,
        ],
    ],
    splitContainer: [
      splitContainerClassName,
      {
        pointerEvents: "none",
        display: "flex",
        flexWrap: "nowrap",
        justifyContent: "center",
        alignItems: "center",
        height: controlStylingConstants.menuItemHeight,
        border: "1px solid transparent",
        boxSizing: "border-box",
        outline: "0px solid transparent",
        cursor: "default",
      },
      !checked && {
        selectors: {
          [`${baseFabricFocusSelector}`]: [
            {
              borderColor: standardControlSemanticColors.focusedBorderColor,
              backgroundColor:
                standardControlSemanticColors.menuFocusedBackgroundColor,
              selectors: {
                [`.${splitPrimaryInMenuClassName}`]: [
                  {
                    height: menuItemHeightWithoutBorder,
                  },
                  commonHighContrastHoverFocusStyles,
                  expandedCheckedPrimaryButtonIconStyle,
                ],
                [`.${splitExpandButtonInMenuClassName}`]: {
                  height: menuItemHeightWithoutBorder,
                  selectors: {
                    [HighContrastSelector]: {
                      backgroundColor: "transparent", // needed to avoid background color from appearing
                      color: "HighlightText",
                      outlineColor: "inherit",
                      ...getHighContrastNoAdjustStyle(),
                    },
                  },
                },
              },
            },
            commonHighContrastOutlineHoverFocus,
          ],
        },
      },
      // For the state when only the menu button is enabled
      !disabled &&
        primaryDisabled && {
          selectors: {
            [`${baseFabricFocusSelector}`]: {
              selectors: {
                [`.${splitExpandButtonInMenuClassName}`]: [
                  {
                    backgroundColor:
                      standardControlSemanticColors.menuFocusedBackgroundColor,
                    height: menuItemHeightWithoutBorder,
                  },
                ],
              },
            },
          },
        },
      // For the expanded state when only the menu button is enabled
      !disabled &&
        primaryDisabled &&
        expanded && [
          {
            selectors: {
              [`.${splitPrimaryInMenuClassName}`]:
                commonHighContrastDisabledStyles,
              [`.${splitExpandButtonInMenuClassName}`]: [
                {
                  backgroundColor:
                    standardControlSemanticColors.selectedBackgroundColor,
                  border: `1px solid ${standardControlSemanticColors.selectedBorderColor}`,
                },
                commonHighContrastHoverFocusStyles,
              ],
            },
          },
        ],
      (disabled || primaryDisabled) && {
        selectors: {
          [`${baseFabricFocusSelector}`]: [
            {
              backgroundColor:
                standardControlSemanticColors.defaultBackgroundColor,
              selectors: {
                [`.${splitPrimaryInMenuClassName}`]:
                  commonHighContrastFocusedDisabledStyles,
              },
            },
            commonHighContrastOutlineFocusedDisabled,
          ],
        },
      },
      disabled && {
        selectors: {
          [HighContrastSelector]: {
            selectors: {
              "svg > path.OfficeIconColors_HighContrast": {
                fill: "GrayText",
              },
            },
          },
        },
      },
      !disabled && {
        selectors: {
          ":active": {
            selectors: {
              [`.${splitExpandButtonInMenuClassName}`]: [
                commonHighContrastHoverFocusStyles,
                splitButtonExpandedCheckedHCIconStyle,
              ],
            },
          },
        },
      },
      expanded && [
        {
          backgroundColor:
            standardControlSemanticColors.expandedBackgroundColor,
          selectors: {
            ":active": {
              selectors: {
                [`.${splitPrimaryInMenuClassName}`]: [
                  {
                    backgroundColor: `${standardControlSemanticColors.pressedBackgroundColor}`,
                  },
                  commonHighContrastHoverFocusStyles,
                ],
              },
            },
          },
        },
        commonHighContrastOutlineToggledOn,
        splitButtonExpandedCheckedHCIconStyle,
      ],
      checked && [
        {
          // Due to us only wanting to add a toggle background to the primary button
          // we also need to change the left border of the split button container to match the toggle color
          borderLeftColor: standardControlSemanticColors.toggledBackgroundColor,
          selectors: {
            [`${baseFabricFocusSelector}`]: [
              {
                borderColor: standardControlSemanticColors.focusedBorderColor,
                backgroundColor:
                  standardControlSemanticColors.toggledBackgroundColor,
                selectors: {
                  [`.${splitPrimaryInMenuClassName}`]: [
                    {
                      height: menuItemHeightWithoutBorder,
                    },
                    commonHighContrastHoverFocusStyles,
                  ],
                  [`.${splitExpandButtonInMenuClassName}`]: [
                    {
                      height: menuItemHeightWithoutBorder,
                      // overrides the case when the button is checked
                      backgroundColor:
                        standardControlSemanticColors.toggledBackgroundColor,
                    },
                    commonHighContrastHoverFocusStyles,
                    splitButtonExpandedCheckedHCIconStyle,
                  ],
                },
              },
              commonHighContrastOutlineHoverFocus,
            ],
          },
        },
        commonHighContrastOutlineToggledOn,
      ],
      checked &&
        !disabled && {
          selectors: {
            [`${containerHoverSelectorFromExpanded}`]: {
              selectors: {
                [`.${splitPrimaryInMenuClassName}`]: {
                  height: menuItemHeightWithoutBorder,
                },
                [`.${splitExpandButtonInMenuClassName}`]: [
                  {
                    height: menuItemHeightWithoutBorder,
                    selectors: {
                      ":hover": [
                        {
                          backgroundColor:
                            standardControlSemanticColors.hoverBackgroundColor,
                        },
                        commonHighContrastHoverFocusStyles,
                        splitButtonExpandedCheckedHCIconStyle,
                      ],
                    },
                  },
                ],
              },
            },
            ":hover:active": {
              borderColor: standardControlSemanticColors.focusedBorderColor,
              selectors: {
                [`.${splitPrimaryInMenuClassName}`]: {
                  height: menuItemHeightWithoutBorder,
                },
                [`.${splitExpandButtonInMenuClassName}`]: {
                  height: menuItemHeightWithoutBorder,
                },
              },
            },
          },
        },
      getMenuButtonStateStyles(theme, expanded, disabled),
    ],
    subMenuIcon: [
      {
        display: "flex",
        width: 38,
        height: 16,
        justifyContent: "center",
        fill: isThemingEnabled()
          ? standardControlSemanticColors.textColor
          : "#444444",
      },
      disabled && {
        opacity: 0.4,
        selectors: {
          [HighContrastSelector]: {
            opacity: 1,
          },
        },
      },
    ],
  });
};

export const getClassNamesForSplitMenuButton = (
  customMenuItemStyles?: Partial<IMenuItemStyles> | ControlInMenuStylesFunction
) => {
  return memoizeFunction(
    (
      theme: ITheme,
      disabled: boolean,
      expanded: boolean,
      checked: boolean,
      _: boolean,
      __: boolean,
      ___?: string,
      ____?: string,
      _____?: string,
      ______?: string,
      primaryDisabled?: boolean
    ): IMenuItemClassNames => {
      return mergeStyleSets(
        getClassNamesForMenuButton()(theme, disabled, expanded),
        redlineClassNamesForSplitMenuButton(
          theme,
          disabled,
          expanded,
          checked,
          primaryDisabled
        ),
        customMenuItemStyles
          ? getStylesFromControlInMenuFnOrObject(
              customMenuItemStyles,
              disabled,
              expanded,
              checked,
              primaryDisabled
            )
          : customMenuItemStyles
      );
    }
  );
};

export const getClassNamesForMLRMenuSplitButton = memoizeFunction(
  (
    customMenuItemStyles?: Partial<IMenuItemStyles>
  ): Partial<IMenuItemClassNames> => {
    const splitButtonHeightStyle = {
      height: controlStylingConstants.smallMenuItemHeight,
      lineHeight: controlStylingConstants.smallMenuItemHeight,
    };
    return mergeStyleSets(
      {
        splitContainer: [
          splitButtonHeightStyle,
          {
            overflow: "hidden",
            selectors: {
              [`& .${dividerClassName}`]: {
                height: 16,
              },
            },
          },
        ],
        splitPrimary: splitButtonHeightStyle,
        splitMenu: [
          splitButtonHeightStyle,
          {
            width: 28,
          },
        ],
      },
      customMenuItemStyles
    );
  }
);
